<template>
  <ErroConexao v-if="mostrarErroConexao" />

  <v-container
    v-else
    class="links-container py-12"
  >
    <TitleHeader>Editais</TitleHeader>

    <!-- Breadcrumb -->
    <nav class="breadcrumb mb-6">
      <NuxtLink
        to="/"
        class="link-anchor"
      >
        <v-icon
          small
          class="mr-1"
          >mdi-home</v-icon
        >
        Início
      </NuxtLink>
      <v-icon
        size="small"
        class="mx-2 text-medium-emphasis"
        >mdi-chevron-right</v-icon
      >
      <NuxtLink
        to="/funarpen/publicacoes"
        class="link-anchor"
      >
        <v-icon
          small
          class="mr-1"
          >mdi-book-open-variant</v-icon
        >
        Publicações
      </NuxtLink>
      <v-icon
        size="small"
        class="mx-2 text-medium-emphasis"
        >mdi-chevron-right</v-icon
      >
      <span class="current">
        <v-icon
          small
          class="mr-1"
          >mdi-book-open-page-variant-outline</v-icon
        >
        Editais
      </span>
    </nav>

    <v-list
      dense
      v-if="resultado && resultado.length"
    >
      <v-list-item
        v-for="(edital, index) in resultado"
        :key="index"
        class="mb-8 link-item"
      >
        <v-list-item>
          <!-- Título -->
          <v-list-item-title class="link-title mb-2">
            <v-icon
              size="20"
              color="primary"
              class="title-icon"
              >mdi-file-document</v-icon
            >
            <span class="title-text">{{ edital.titulo }}</span>
          </v-list-item-title>

          <!-- Descrição -->
          <div
            class="edital-descricao mb-4"
            v-html="edital.descricao"
          />

          <!-- Arquivos -->
          <v-list
            dense
            class="arquivo-lista"
          >
            <v-list-item
              v-for="(arquivo, idx) in ordenarArquivos(edital.arquivos)"
              :key="idx"
              class="arquivo-item"
            >
              <v-list-item>
                <div class="arquivo-topo d-flex align-center flex-wrap">
                  <NuxtLink
                    :to="getDownloadLink(arquivo)"
                    target="_blank"
                    rel="noopener"
                    class="link-anchor arquivo-nome"
                    >{{ formatarNomeArquivo(arquivo.nome) }}</NuxtLink
                  >
                  <span class="arquivo-data ml-3">
                    {{ formatarData(arquivo.dataPublicacao) }}
                  </span>
                </div>
                <div class="arquivo-descricao">{{ arquivo.descricao }}</div>
              </v-list-item>
            </v-list-item>
          </v-list>
        </v-list-item>
      </v-list-item>
    </v-list>

    <div
      v-else-if="loading"
      class="loading-state"
      aria-live="polite"
      role="status"
    >
      <div
        class="spinner"
        aria-hidden="true"
      ></div>
      <p class="loading-text">Carregando informações...</p>
    </div>

    <div v-else-if="erro">{{ erro }}</div>

    <div
      v-else
      class="empty-state"
    >
      <div
        class="empty-icon"
        aria-hidden="true"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6m7 1.5L18.5 9H13V3.5M8 13h8v2H8v-2m0 4h8v2H8v-2m0-8h5v2H8V9Z"
          />
        </svg>
      </div>
      <h2 class="empty-title">Nenhum edital encontrado</h2>
      <p class="empty-description">Ainda não há registros disponíveis nesta seção<br /></p>
    </div>
  </v-container>
</template>

<script setup>
import { formatarNomeArquivo } from '@/utils/arquivo'
import { ref, onMounted } from 'vue'
import { useRuntimeConfig } from '#imports'
import { useRouter } from 'vue-router'
import ErroConexao from '~/components/ErroConexao.vue'
import TitleHeader from '@/components/TitleHeader.vue'

definePageMeta({
  middleware: 'manutencao',
  title: 'FUNARPEN - Publicações - Editais',
})

useHead({
  title: 'Editais',
  meta: [{ name: 'description', content: 'Editais' }],
})

const router = useRouter()
const mostrarErroConexao = ref(false)
const config = useRuntimeConfig()
const baseUrl = config.public.backofficeApiBaseUrl
const resultado = ref([])
const loading = ref(true)
const erro = ref('')

async function buscarEditais() {
  loading.value = true
  erro.value = ''

  try {
    const res = await fetch(`${baseUrl}/portal/paginas/buscar-subsecao?subsecao=editais`)

    if (res.status === 401) {
      router.push('/manutencao/acesso-negado')
      return
    } else if (res.status === 404) {
      resultado.value = []
      return
    }

    if (!res.ok) throw new Error('Erro ao buscar dados')

    const data = await res.json()
    resultado.value = Array.isArray(data) ? data : []
  } catch (e) {
    if (e.message.includes('Failed to fetch')) {
      mostrarErroConexao.value = true
    } else {
      erro.value = e.message
    }
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  buscarEditais()
})

function ordenarArquivos(arquivos) {
  return [...arquivos].sort((a, b) => a.ordem - b.ordem)
}

function formatarData(dataIso) {
  if (!dataIso) return ''
  const data = new Date(dataIso)
  return data.toLocaleDateString('pt-BR')
}

function getDownloadLink(arquivo) {
  return `${baseUrl}/portal/arquivos/baixar/${arquivo.id}`
}

</script>

<style scoped>
/* Container geral */
.links-container {
  box-sizing: border-box;
  min-width: 320px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding-left: 2rem;
  padding-right: 2rem;

  /* Quebra de texto para palavras grandes e URLs */
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Aplicar palavra e quebra em todos os descendentes para evitar overflow */
.links-container,
.links-container * {
  word-break: break-word;
  overflow-wrap: anywhere;
}

/* Título dos editais com ícone - flex com wrap para quebra */
.link-title {
  font-weight: 700;
  text-transform: uppercase;
  color: var(--funarpen-blue);
  font-size: 1.1rem;

  display: flex;
  align-items: center;
  flex-wrap: wrap;
  /* Permite quebra de linha */

  word-break: break-word;
}

/* Ícone do título não encolhe e tem margem */
.title-icon {
  flex-shrink: 0;
  margin-right: 0.5rem;
}

/* Texto do título permite quebra e ocupa espaço */
.title-text {
  white-space: normal;
  flex: 1 1 auto;
}

/* Links */
.link-anchor {
  color: var(--funarpen-blue);
  text-decoration: none;
  transition: color 0.3s ease;
}

.link-anchor:hover,
.link-anchor:focus-visible {
  color: var(--funarpen-blue-2);
  text-decoration: underline;
}

/* Lista de arquivos */
.arquivo-lista {
  margin-top: 0.5rem;
}

.arquivo-item {
  padding: 0.5rem 0;
  border-top: 1px solid rgba(11, 69, 113, 0.1);
}

.arquivo-topo {
  font-size: 1rem;
  font-weight: 600;
  color: var(--funarpen-blue);
}

.arquivo-data {
  font-size: 0.9rem;
  color: var(--funarpen-muted);
}

.arquivo-descricao {
  font-size: 0.9rem;
  color: var(--funarpen-muted);
  margin-top: 0.2rem;
}

/* Descrição do edital */
.edital-descricao {
  font-size: 1rem;
  color: var(--funarpen-ink);
}

/* =====================
   BREADCRUMB
======================== */
.breadcrumb {
  font-size: 0.9rem;
  display: flex;
  align-items: center;

  /* permite quebra de linha */
  flex-wrap: wrap;
  justify-content: flex-start;

  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

.breadcrumb .link-anchor {
  text-decoration: none;
  color: var(--funarpen-muted);
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  border-radius: 6px;

  /* cada item não quebra sozinho, mas o conjunto pode ir pra próxima linha */
  white-space: nowrap;
}

.breadcrumb .link-anchor:hover {
  color: var(--funarpen-blue-2);
  background: rgba(11, 79, 108, 0.05);
}

.breadcrumb .sep {
  margin: 0 0.5rem;
  color: var(--funarpen-muted);
  user-select: none;
}

.breadcrumb .current {
  color: var(--funarpen-ink);
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

/* ===========================================
   Nenhum resultado
=========================================== */
.empty-state {
  max-width: 500px;
  margin: 3rem auto;
  padding: 2.5rem 2rem;
  background: rgba(11, 69, 113, 0.04);
  border: 1px solid rgba(11, 69, 113, 0.15);
  border-radius: var(--funarpen-radius);
  text-align: center;
  box-shadow: 0 8px 24px rgba(11, 69, 113, 0.08);
  color: rgba(11, 69, 113, 0.85);
  font-family: Inter, 'Segoe UI', Roboto, Arial, sans-serif;
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.empty-icon svg {
  width: 72px;
  height: 72px;
  fill: rgba(11, 69, 113, 0.6);
  filter: drop-shadow(0 2px 4px rgba(11, 69, 113, 0.2));
}

.empty-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: rgba(11, 69, 113, 0.9);
}

.empty-description {
  font-size: 1.125rem;
  font-weight: 400;
  color: rgba(11, 69, 113, 0.75);
  line-height: 1.6;
}

/* ===========================================
   Carregamento
=========================================== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: rgba(11, 69, 113, 0.8);
  font-family: Inter, 'Segoe UI', Roboto, Arial, sans-serif;
}

/* Spinner circular elegante */
.spinner {
  width: 64px;
  height: 64px;
  border: 6px solid rgba(11, 69, 113, 0.2);
  border-top-color: rgba(11, 69, 113, 0.7);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1.25rem;
  font-weight: 500;
  color: rgba(11, 69, 113, 0.85);
}

/* Responsivo */
@media (max-width: 600px) {
  .link-title {
    font-size: 1rem;
  }

  .arquivo-topo {
    flex-direction: column;
    align-items: flex-start;
  }

  .arquivo-nome {
    font-size: 1rem;
  }

  .arquivo-data {
    margin-left: 0;
    margin-top: 0.3rem;
    font-size: 0.85rem;
  }

  .arquivo-descricao {
    font-size: 0.85rem;
  }

  .edital-descricao {
    font-size: 0.95rem;
  }

  /* ===========================================
   Nenhum resultado
  =========================================== */
  .empty-state {
    padding: 2rem 1.2rem;
  }

  .empty-title {
    font-size: 1.5rem;
  }

  .empty-description {
    font-size: 1rem;
  }

  .empty-icon svg {
    width: 60px;
    height: 60px;
  }

  /* ===========================================
   Carregamento
  =========================================== */
  .spinner {
    width: 48px;
    height: 48px;
    border-width: 5px;
  }

  .loading-text {
    font-size: 1.125rem;
  }

  /* breadcrumb mais compacto */
  .breadcrumb {
    font-size: 0.8rem;
  }

  .breadcrumb .link-anchor {
    padding: 3px 4px;
  }

  .breadcrumb .v-icon {
    font-size: 16px;
    margin: 0 2px;
  }
}

@media (max-width: 400px) {
  /* breadcrumb ultra compacto */
  .breadcrumb {
    font-size: 0.7rem;
  }

  .breadcrumb .v-icon {
    font-size: 14px;
  }
}
</style>
